/** Hall voice groups, connect rules, and stage-stair access. */

export const VOICE_GAIN = 2.25;
export const TEAM_SEAT_SIT_Y = 1455;
export const TEAM_SEAT_RADIUS = 70;
/** Top of the stage barrier (map y grows downward). y < this is on stage. */
export const STAGE_BARRIER_Y = 1544;
export const STAGE_STAIR_NAMES = ['stage entrance left', 'stage entrance right'];

export function isCreatorHall(hall) {
  if (!hall) return false;
  return hall.roomType === 'user' || String(hall.slug || '').startsWith('@');
}

export function hasStageAccess({
  isCreatorHall: creatorHall,
  membership,
  user,
  ownerUserId
} = {}) {
  if (!creatorHall) return false;
  if (user?.status === 'Owner') return true;
  if (membership?.role === 'creator') return true;
  if (
    ownerUserId != null
    && user?.userId != null
    && String(ownerUserId) === String(user.userId)
  ) {
    return true;
  }
  return !!membership?.isVip;
}

/** Click-to-sit on LAN desks. Commons is open; creator halls need stage access. */
export function canUseLanSeats(opts = {}) {
  if (!opts.isCreatorHall) return true;
  return hasStageAccess(opts);
}

export function lanTeamAt(
  x,
  y,
  teamSeats,
  sitY = TEAM_SEAT_SIT_Y,
  radius = TEAM_SEAT_RADIUS
) {
  if (!teamSeats || !teamSeats.length) return null;
  const r2 = radius * radius;
  for (const seat of teamSeats) {
    const dx = x - seat.cx;
    const dy = y - sitY;
    if (dx * dx + dy * dy < r2) return seat.team || null;
  }
  return null;
}

/**
 * @returns {'lanA'|'lanB'|'stage'|'floor'}
 */
export function voiceGroup({
  x,
  y,
  teamSeats,
  sitY = TEAM_SEAT_SIT_Y
} = {}) {
  const team = lanTeamAt(x, y, teamSeats, sitY);
  if (team === 'A') return 'lanA';
  if (team === 'B') return 'lanB';
  if (Number(y) < STAGE_BARRIER_Y) return 'stage';
  return 'floor';
}

/**
 * Commons / global halls: LAN A only hears LAN A, LAN B only LAN B.
 * Everyone else (audience, standing, on-stage walkers) hears each other
 * at full gain, not the LAN huddles.
 *
 * Creator halls: floor has no voice. LAN A/B same huddle rules, plus
 * on-stage walkers hear both teams and each other.
 */
export function shouldVoiceConnect(groupA, groupB, creatorHall) {
  if (!groupA || !groupB) return false;
  if (groupA === 'lanA' && groupB === 'lanA') return true;
  if (groupA === 'lanB' && groupB === 'lanB') return true;

  if (creatorHall) {
    if (groupA === 'floor' || groupB === 'floor') return false;
    if (groupA === 'stage' && (groupB === 'stage' || groupB === 'lanA' || groupB === 'lanB')) {
      return true;
    }
    if (groupB === 'stage' && (groupA === 'lanA' || groupA === 'lanB')) {
      return true;
    }
    return false;
  }

  const open = (g) => g === 'floor' || g === 'stage';
  return open(groupA) && open(groupB);
}

export function voiceGainFor(groupA, groupB, creatorHall, globalVolume = 1) {
  if (!shouldVoiceConnect(groupA, groupB, creatorHall)) return 0;
  const g = Number(globalVolume);
  const vol = Number.isFinite(g) ? g : 1;
  return VOICE_GAIN * vol;
}

export function isTeamSeatOccupied(seat, players, {
  sitY = TEAM_SEAT_SIT_Y,
  radius = TEAM_SEAT_RADIUS,
  ignoreId = null
} = {}) {
  if (!seat) return false;
  const r2 = radius * radius;
  return Object.values(players || {}).some((p) => {
    if (!p || p.id == null) return false;
    if (ignoreId != null && String(p.id) === String(ignoreId)) return false;
    const dx = p.x - seat.cx;
    const dy = p.y - sitY;
    return dx * dx + dy * dy < r2;
  });
}

export function findFreeTeamSeat(teamSeats, players, team, options = {}) {
  const seats = (teamSeats || []).filter((s) => s.team === team);
  return seats.find((seat) => !isTeamSeatOccupied(seat, players, options)) || null;
}

export function teamHasFreeSeat(teamSeats, players, team, options = {}) {
  return !!findFreeTeamSeat(teamSeats, players, team, options);
}
